<template>
    <div class="inline-flex">
        <div class="btn btn-sm btn-blue" @click="openModal">
            Indicators
            <i class="fas fa-caret-down ml-1"></i>
        </div>

        <modal @close="handleClose" ref="modalRef" v-if="showModal" id="modalIndicators">
            <template #header>
                <span v-if="selectedCIndicator">{{ getIndicatorName() }}</span>
                <span v-else>Indicators</span>
            </template>
            <template #body>
                <div class="indicators-wrapper">
                    <div class="flex flex-wrap" v-if="!selectedCIndicator">
                        <div class="w-full p-0 mb-3" v-if="currentIndicators.length > 0">
                            <div class="column-title pb-px border-b">Currently on Chart</div>
                            <ul class="list-none p-0 current-indicators">
                                <li v-for="(iIndicator, iIndex) in currentIndicators">
                                    <span @click="removeIndicator(iIndex, iIndicator)" class="cursor">
                                        <i class="bc-glyph-times text-red-500 cursor-pointer"></i>
                                    </span>
                                    <a @click.prevent="showExistingIndicator(iIndicator, iIndex)" href="">{{ iIndicator.title }}</a>
                                </li>
                            </ul>
                        </div>

                        <div class="w-full p-0">
                            <input v-focus placeholder="Search for indicator..." class="form-input-inline" type="text" name="search" v-model="search">
                            <div class="indicator-list">
                                <!-- Overlay Indicators -->
                                <h4 v-if="groupedIndicators.overlay.length > 0" class="mt-2 mb-1 pb-px border-b">Overlay</h4>
                                <ul class="list-none p-0 overlay-list">
                                    <li @click="showIndicator(iIndicator)" v-for="iIndicator in groupedIndicators.overlay">
                                        <i class="fas fa-layer-group mr-1"></i>{{ iIndicator.meta.title }}
                                    </li>
                                </ul>

                                <!-- Standalone Indicators -->
                                <h4 v-if="groupedIndicators.standalone.length > 0" class="mt-2 mb-1 pb-px border-b">Indicators</h4>
                                <ul class="list-none p-0 standalone-list">
                                    <li @click="showIndicator(iIndicator)" v-for="iIndicator in groupedIndicators.standalone">
                                        <i class="fas fa-plus-circle mr-1"></i>{{ iIndicator.meta.title }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <selected-indicator
                        v-if="selectedCIndicator"
                        v-model="modelIndicator"
                        :indicator="selectedCIndicator"
                        :default-placement="defaultPlacement"
                    />

                    <div class="flex justify-start mt-2">
                        <div v-if="hasExistingIndicator()" class="btn btn-sm btn-white-ghost inline-flex mr-1" @click="cloneIndicator">Clone Indicator</div>
                        <div v-if="selectedCIndicator" class="btn btn-sm btn-white-ghost inline-flex" @click="resetIndicator">Reset</div>
                    </div>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-center">
                    <div v-if="hasExistingIndicator()" class="btn btn-sm btn-red inline-flex" @click="removeSelectedIndicator">Delete</div>
                    <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="handleClose">Cancel</div>
                    <div class="btn btn-sm btn-yellow inline-flex mx-1" v-if="showModal !== 'tools'" @click="draw">Draw</div>
                </div>
            </template>
        </modal>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import SelectedIndicator from './SelectedIndicator';

const props = defineProps({
    chartInstance: Object,
});

const $clone = (obj) => JSON.parse(JSON.stringify(obj));

const emit = defineEmits(['remove', 'apply-indicators']);

const indicators = ref(getIndicators());
const search = ref('');
const selectedCIndicator = ref(false);
const defaultPlacement = ref('overlay');

const modelIndicator = ref({});
const action = ref('Add');
const indicatorIndex = ref(0);

const currentIndicators = ref({ });
const showModal = ref(false);

// computed
const filteredIndicators = computed(() => {
    const sortByName = (a, b) => {
        const titleA = a.meta.title.toLowerCase();
        const titleB = b.meta.title.toLowerCase();

        if (titleA < titleB) return -1;
        else if (titleA > titleB) return 1;
        else return 0;
    };

    if (!search.value) {
        return indicators.value.sort(sortByName);
    }

    const searchQueries = search.value.toLowerCase().split(' ');

    return indicators.value.filter((mIndicator) => {
        return searchQueries.every((search) => {
            return mIndicator.meta.title.split(' ').some((partialName) => {
                return partialName.toLowerCase().startsWith(search);
            });
        });

    }).sort(sortByName);
});

const groupedIndicators = computed(() => {
    const fiIndicators = filteredIndicators.value;

    return fiIndicators.reduce((obj, mIndicator) => {
        if (mIndicator.meta.overlay) {
            obj.overlay.push(mIndicator);
        } else {
            obj.standalone.push(mIndicator);
        }

        return obj;
    }, {overlay: [], standalone: []});
});

const actions = computed(() => {
    const context = {
        curves: modelIndicator.value.curves,
        inputs: modelIndicator.value.inputs,
        placement: modelIndicator.value.placement,
        studyId: modelIndicator.value.id || modelIndicator.value.studyId,
        type: 'Study',
        id: action.value,
        index: indicatorIndex.value
    };

    if (modelIndicator.value.levels) {
        context.levels = modelIndicator.value.levels;
    }

    return {
        id: 'Plot',
        context
    };
});

function getIndicators() {
    const fiIndicators = window.Barchart.RealtimeWidgets.Charts.getTaxonomies().studies;

    return fiIndicators.map((mIndicator) => {
        if (mIndicator.meta.title === 'TrendSpotter') {
            mIndicator.meta.title = 'Trend Seeker';
            mIndicator.meta.caption = 'Trend Seeker';
        }

        return mIndicator;
    });
}

const openModal = () => {
    currentIndicators.value = props.chartInstance.change({
        id: 'Plot',
        context: {
            id: 'Get',
            type: 'Study'
        }
    });
    showModal.value = true;
};

const handleClose = () => {
    showModal.value = false;
    selectedCIndicator.value = false;
};

const getIndicator = (id) => {
    return indicators.value.find(i => i.id === id);
};

const showExistingIndicator = (pIndicator, pIndex) => {
    const indicatorDefaults = getIndicator(pIndicator.studyId);

    pIndicator.meta = indicatorDefaults.meta;
    showModal.value = true;
    defaultPlacement.value = indicatorDefaults.meta.overlay ? 'overlay' : 'standalone';

    // showIndicator(indicator);
    action.value = 'Update';
    indicatorIndex.value = pIndex;
    selectedCIndicator.value = pIndicator;
};

const normalizeIndicator = (pIndicator) => {
    let normalized = $clone(pIndicator.defaults);
    normalized.id = pIndicator.id;
    normalized.meta = pIndicator.meta;

    return normalized;
};

const showIndicator = (pIndicator) => {
    selectedCIndicator.value = normalizeIndicator(pIndicator);
    action.value = 'Add';
    search.value = '';

    // console.log(`Showing indicator for: [${pIndicator.title || pIndicator.meta.title}]`);

    const defaultsFromLocalStorage = window.localStorage.getItem(pIndicator.id);
    if (defaultsFromLocalStorage) {
        // console.log(`With defaults: [${defaultsFromLocalStorage}]`);
        // console.log('Merging defaults');
        selectedCIndicator.value = Object.assign(selectedCIndicator.value, JSON.parse(defaultsFromLocalStorage));
    }

    defaultPlacement.value = pIndicator.meta.overlay ? 'overlay' : 'standalone';
    selectedCIndicator.value.placement = getIndicatorPlacement(pIndicator.meta);
};

const getIndicatorPlacement = (meta, pIndicator) => {
    if (meta.overlay) {
        return 'overlay';
    } else {
        return pIndicator && pIndicator.placement !== 'standalone' ? 'withMain' : 'standalone';
    }
};

const hasExistingIndicator = () => {
    return selectedCIndicator.value && selectedCIndicator.value.index !== undefined;
};

const removeSelectedIndicator = () => {
    removeIndicator(selectedCIndicator.value.index, selectedCIndicator.value);
};

const removeIndicator = (pIndex, pIndicator) => {
    emit('remove', pIndex, pIndicator);
    handleClose();
};

const cloneIndicator = () => {
    const cloned = $clone(modelIndicator.value);

    delete cloned.index;
    cloned.isClone = true;

    selectedCIndicator.value = cloned;
    cloned.defaults = $clone(cloned);
    cloned.id = cloned.studyId;

    // Normalize indicator settings
    showIndicator(cloned);
};

const resetIndicator = () => {
    const cIndicator = getIndicator(modelIndicator.value.studyId) || getIndicator(modelIndicator.value.id);

    if (cIndicator) {
        const normalized = normalizeIndicator(cIndicator);
        Object.keys(normalized).map(key => {
            modelIndicator.value[key] = $clone(normalized[key]);
        });

        window.localStorage.removeItem(modelIndicator.value.studyId);
    }
};

const storeIndicatorDefaultsInLocalStorage = () => {
    window.localStorage.setItem(actions.value.context.studyId, JSON.stringify({
        curves: actions.value.context.curves,
        inputs: actions.value.context.inputs,
        placement: actions.value.context.placement
    }));
};

const draw = () => {
    emit('apply-indicators', actions.value);
    handleClose();
    storeIndicatorDefaultsInLocalStorage();
};

const getIndicatorName = () => {
    const cIndicator = indicators.value.find(i => i.id === (selectedCIndicator.value.studyId || selectedCIndicator.value.id));
    const suffix = (selectedCIndicator.value && selectedCIndicator.value.isClone) ? ' - (cloned)' : '';

    if (!cIndicator) {
        return selectedCIndicator.value.title + suffix;
    } else {
        return cIndicator.meta.title + suffix;
    }
};

defineExpose({
    openModal,
    showExistingIndicator,
});
</script>

<style lang="scss">
#modalIndicators {
    .modal-container {
        width: 24rem;
        @screen xs {
            width: 100%;
        }
    }

    .indicators-wrapper {
        @apply m-0 mt-2 mb-2 p-0 text-sm;
        .column-title {
            @apply font-bold mb-1;
        }

        .indicator-list {
            overflow-y: scroll;
            overflow-x: hidden;
            @apply h-64 px-2 pb-2 border border-gray-400 border-dotted border-t-0;

            li {
                @apply text-sm  my-1;
                @apply cursor-pointer;
                i {
                    @apply text-gray-400;
                }
                &:hover {
                    @apply text-blue-500 underline;
                    i {
                        @apply text-blue-500;
                    }
                }
            }
        }

    }
}
</style>
